import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusChip from "@/components/StatusChip";
import { FileText, Plus, Download, Cog, ArrowRightCircle, FlaskConical, Factory, Search } from "lucide-react";
import { toast } from "sonner";

const STAGE_META = {
  pp_gold_seal: { label: "PP / Gold Seal OB", icon: FlaskConical, testid: "tab-stage-pp" },
  production: { label: "Production OB", icon: Factory, testid: "tab-stage-prod" },
};

export default function BulletinList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [styles, setStyles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [buyerFilter, setBuyerFilter] = useState("all");
  const [styleSearch, setStyleSearch] = useState("");
  const [stage, setStage] = useState("pp_gold_seal");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ buyer: "", our_styles: [], buyer_style: "" });
  const [dialogStyleQuery, setDialogStyleQuery] = useState("");

  const load = async () => {
    const { data } = await api.get("/bulletins");
    setItems(data);
  };
  useEffect(() => {
    load();
    (async () => {
      const [b, s] = await Promise.all([api.get("/masters/buyers"), api.get("/masters/styles")]);
      setBuyers(b.data); setStyles(s.data);
    })();
  }, []);

  const counts = useMemo(() => ({
    pp_gold_seal: items.filter((b) => (b.stage || "pp_gold_seal") === "pp_gold_seal").length,
    production: items.filter((b) => b.stage === "production").length,
  }), [items]);

  const filtered = useMemo(() => {
    const q = styleSearch.trim().toLowerCase();
    // Build a fast lookup of styles whose description or product-group name
    // contains the search text. Any bulletin whose primary or clubbed colourway
    // resolves to one of those styles will match — so users can find a bulletin
    // by the words in its style Description.
    const stylesMatchingDesc = q
      ? new Set(
          (styles || [])
            .filter((s) => {
              const desc = (s.description || "").toLowerCase();
              const nm = (s.name || "").toLowerCase();
              return desc.includes(q) || nm.includes(q);
            })
            .map((s) => (s.our_style || "").toLowerCase())
            .filter(Boolean)
        )
      : null;
    return items.filter((b) => {
      if ((b.stage || "pp_gold_seal") !== stage) return false;
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (buyerFilter !== "all" && b.buyer !== buyerFilter) return false;
      if (q) {
        const pool = [b.our_style, ...(Array.isArray(b.our_styles) ? b.our_styles : []), b.buyer_style]
          .filter(Boolean)
          .map((s) => String(s).toLowerCase());
        const directHit = pool.some((s) => s.includes(q));
        const descHit = stylesMatchingDesc && pool.some((s) => stylesMatchingDesc.has(s));
        if (!directHit && !descHit) return false;
      }
      return true;
    });
  }, [items, stage, statusFilter, buyerFilter, styleSearch, styles]);

  const create = async () => {
    if (!form.buyer || (form.our_styles || []).length === 0) { toast.error("Buyer and at least one style are required"); return; }
    try {
      const { data } = await api.post("/bulletins", { ...form, stage, operations: [] });
      const n = (form.our_styles || []).length;
      toast.success(`${STAGE_META[stage].label} created${n > 1 ? ` — ${n} colourways clubbed` : ""}`);
      setOpen(false);
      setForm({ buyer: "", our_styles: [], buyer_style: "" });
      setDialogStyleQuery("");
      nav(`/bulletins/${data.id}`);
    } catch { toast.error("Create failed"); }
  };

  const exportCsv = () => {
    const headers = ["Stage", "Buyer", "Our Style", "Buyer Style", "Version", "SAM Cost", "Part Rate Cost", "Status", "Updated"];
    const rows = filtered.map((b) => [STAGE_META[b.stage || "pp_gold_seal"].label, b.buyer, b.our_style, b.buyer_style, b.version, b.sam_cost_total, b.op_cost_total, b.status, b.updated_at?.slice(0, 10)]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" }); const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `bulletins-${stage}-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    toast.success("Exported CSV");
  };

  const StageIcon = STAGE_META[stage].icon;

  return (
    <div className="space-y-6" data-testid="bulletins-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Operation Bulletin</div>
          <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">Bulletin Library</h1>
          <p className="text-slate-500 text-[14px] mt-1">Sample bulletins get approved as PP/Gold Seal, then transferred to Production for the shop floor.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/bulletins/machines")} data-testid="bulletins-machines-btn"><Cog className="h-4 w-4 mr-2" /> Machine Master</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="bulletins-export-btn"><Download className="h-4 w-4 mr-2" /> Export</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand hover:bg-brand-dark text-white" data-testid="bulletin-create-btn"><Plus className="h-4 w-4 mr-2" /> New {STAGE_META[stage].label}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><StageIcon className="h-5 w-5 text-brand" /> Create {STAGE_META[stage].label}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {stage === "production" && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900">
                    Tip: Production OBs are usually created by <span className="font-semibold">transferring an approved PP/Gold Seal OB</span>. Only create one manually if there is no corresponding sample bulletin.
                  </div>
                )}
                <div>
                  <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">Buyer</label>
                  <Select value={form.buyer} onValueChange={(v) => setForm({ ...form, buyer: v })}>
                    <SelectTrigger data-testid="new-bulletin-buyer" className="mt-1"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                    <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">Our Style Number(s)</label>
                  <div className="text-[11px] text-slate-500 mb-1.5">Search by Our Style OR Buyer Style Number — the Buyer + Buyer Style auto-fill from your first pick. Tick every colourway of the same style you want to club under this OB.</div>
                  {(() => {
                    const q = dialogStyleQuery.trim().toLowerCase();
                    // Universal search: if a buyer is picked, scope to that buyer; otherwise search ALL styles.
                    const scoped = form.buyer
                      ? (styles || []).filter((s) => (s.buyer || "").trim() === form.buyer.trim())
                      : (styles || []);
                    const filtered = q
                      ? scoped.filter((s) => {
                          const pool = [s.our_style, s.buyer_style, s.name, s.description, s.buyer].filter(Boolean).map((v) => String(v).toLowerCase());
                          return pool.some((p) => p.includes(q));
                        })
                      : scoped;
                    // Group by buyer for display when no buyer is picked yet, so results are scannable.
                    const groups = {};
                    filtered.forEach((s) => { const b = s.buyer || "(no buyer)"; (groups[b] = groups[b] || []).push(s); });
                    const groupKeys = Object.keys(groups).sort();
                    if (!q && !form.buyer) {
                      return (
                        <>
                          <div className="relative mb-1.5">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                            <Input
                              placeholder="Type Our Style, Buyer Style, or Buyer name to find styles…"
                              value={dialogStyleQuery}
                              onChange={(e) => setDialogStyleQuery(e.target.value)}
                              className="h-9 pl-8 text-[13px]"
                              data-testid="new-bulletin-style-search"
                            />
                          </div>
                          <div className="text-[12px] text-slate-500 italic px-3 py-3 rounded-md border border-dashed border-slate-200">
                            Start typing a style code, or pick a Buyer above to browse.
                          </div>
                        </>
                      );
                    }
                    return (
                      <>
                        <div className="relative mb-1.5">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                          <Input
                            placeholder={form.buyer ? "Filter by Our Style or Buyer Style within this buyer…" : "Type Our Style, Buyer Style, or Buyer name to find styles…"}
                            value={dialogStyleQuery}
                            onChange={(e) => setDialogStyleQuery(e.target.value)}
                            className="h-9 pl-8 text-[13px]"
                            data-testid="new-bulletin-style-search"
                          />
                        </div>
                        {filtered.length === 0 ? (
                          <div className="px-3 py-6 text-center text-[12px] text-slate-400 rounded-md border border-dashed border-slate-200">
                            {q ? `No styles match "${dialogStyleQuery}"` : `No styles found for ${form.buyer}. Add them under Masters → Styles.`}
                          </div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="new-bulletin-style-list">
                            {groupKeys.map((b) => (
                              <div key={b}>
                                {!form.buyer && (
                                  <div className="px-3 py-1 bg-slate-50 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 sticky top-0">
                                    {b} <span className="font-mono text-slate-400">({groups[b].length})</span>
                                  </div>
                                )}
                                {groups[b].map((s) => {
                                  const checked = (form.our_styles || []).includes(s.our_style);
                                  const buyerConflict = form.buyer && (s.buyer || "").trim() !== form.buyer.trim();
                                  return (
                                    <label
                                      key={s.id}
                                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${checked ? "bg-rose-50/60" : "hover:bg-slate-50"} ${buyerConflict ? "opacity-40" : ""}`}
                                      data-testid={`new-bulletin-style-opt-${s.our_style}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={buyerConflict}
                                        onChange={(e) => {
                                          const set = new Set(form.our_styles || []);
                                          if (e.target.checked) set.add(s.our_style); else set.delete(s.our_style);
                                          const arr = Array.from(set);
                                          // Auto-fill buyer & buyer_style from the first ticked row so the user
                                          // doesn't have to hunt for the buyer picker separately.
                                          let nextBuyer = form.buyer;
                                          let nextBuyerStyle = form.buyer_style || "";
                                          if (arr.length > 0) {
                                            const firstRow = (styles || []).find((x) => x.our_style === arr[0]);
                                            if (!nextBuyer && firstRow?.buyer) nextBuyer = firstRow.buyer;
                                            if (!nextBuyerStyle && firstRow?.buyer_style) nextBuyerStyle = firstRow.buyer_style;
                                          } else {
                                            // Unticking all → don't auto-clear buyer; admin may want to browse another style
                                          }
                                          setForm({ ...form, our_styles: arr, buyer: nextBuyer, buyer_style: nextBuyerStyle });
                                        }}
                                        className="h-4 w-4 accent-brand"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                          <span className="font-mono text-[13px] font-semibold text-brand">{s.our_style}</span>
                                          {s.buyer_style && <span className="font-mono text-[11.5px] text-slate-500">Buyer#: <span className="text-slate-800 font-semibold">{s.buyer_style}</span></span>}
                                          {!form.buyer && s.buyer && <span className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">· {s.buyer}</span>}
                                        </div>
                                        {s.name && <div className="text-[11.5px] text-slate-500 truncate">{s.name}</div>}
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {(form.our_styles || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="new-bulletin-style-chips">
                      {form.our_styles.map((s) => (
                        <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-50 text-brand text-[11.5px] font-mono">
                          {s}
                          <button type="button" onClick={() => setForm({ ...form, our_styles: form.our_styles.filter((x) => x !== s) })} className="hover:text-brand-dark">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">Buyer Style Number</label>
                  <Input value={form.buyer_style} onChange={(e) => setForm({ ...form, buyer_style: e.target.value })} placeholder="e.g. HM-24-POLO-BLU" data-testid="new-bulletin-buyer-style" className="mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="new-bulletin-confirm" className="bg-brand hover:bg-brand-dark text-white">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stage tabs */}
      <Tabs value={stage} onValueChange={setStage} className="w-full">
        <TabsList data-testid="bulletin-stage-tabs">
          {Object.entries(STAGE_META).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <TabsTrigger key={k} value={k} data-testid={m.testid} className="gap-1.5">
                <Icon className="h-3.5 w-3.5" /> {m.label}
                <span className="ml-1.5 text-[10.5px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{counts[k]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.keys(STAGE_META).map((k) => (
          <TabsContent key={k} value={k} className="space-y-4 mt-4">
            <Card className="p-4 border-slate-200 rounded-md bg-white">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Select value={buyerFilter} onValueChange={setBuyerFilter}>
                  <SelectTrigger data-testid="bulletins-buyer-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All buyers</SelectItem>
                    {buyers.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="bulletins-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["all", "Draft", "Under Preparation", "Ready for Approval", "Approved", "Released", "Rejected"].map((s) => (
                      <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={styleSearch}
                  onChange={(e) => setStyleSearch(e.target.value)}
                  placeholder="Search style / colourway / description…"
                  data-testid="bulletins-style-search"
                />
                <div className="text-[12.5px] text-slate-500 self-center">{filtered.length} {STAGE_META[k].label.toLowerCase()}{filtered.length !== 1 ? "s" : ""}</div>
              </div>
            </Card>

            <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="opsiq-table">
                  <thead>
                    <tr>
                      <th>Buyer</th><th>Our Style</th><th>Buyer Style</th><th>Version</th>
                      <th className="text-right">SAM Cost</th><th className="text-right">Part Rate</th>
                      <th>Status</th>
                      {k === "pp_gold_seal" && <th>Transfer</th>}
                      {k === "production" && <th>Source</th>}
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((b) => (
                      <tr key={b.id} data-testid={`bulletin-row-${b.our_style}`} onClick={() => nav(`/bulletins/${b.id}`)} className="cursor-pointer">
                        <td className="font-semibold">{b.buyer}</td>
                        <td className="font-mono font-semibold text-brand">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span>{b.our_style}</span>
                            {Array.isArray(b.our_styles) && b.our_styles.length > 1 && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-50 text-brand text-[10px] font-semibold"
                                title={`Also clubbed: ${b.our_styles.slice(1).join(", ")}`}
                                data-testid={`bulletin-colourways-${b.our_style}`}
                              >
                                +{b.our_styles.length - 1}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="text-slate-600">{b.buyer_style}</td>
                        <td><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[11.5px] font-mono">v{b.version}</span></td>
                        <td className="text-right font-mono font-semibold text-emerald-700">₹ {Number(b.sam_cost_total || 0).toFixed(2)}</td>
                        <td className="text-right font-mono font-semibold text-pink-700">₹ {Number(b.op_cost_total || 0).toFixed(2)}</td>
                        <td><StatusChip status={b.status} size="sm" /></td>
                        {k === "pp_gold_seal" && (
                          <td>
                            {b.transferred_to ? (
                              <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700 font-medium"><ArrowRightCircle className="h-3.5 w-3.5" /> Transferred</span>
                            ) : b.status === "Approved" ? (
                              <span className="text-[11.5px] text-slate-500">Ready</span>
                            ) : (
                              <span className="text-[11.5px] text-slate-400">—</span>
                            )}
                          </td>
                        )}
                        {k === "production" && (
                          <td className="text-[11.5px] text-slate-500 font-mono">{b.transferred_from?.our_style || "—"}</td>
                        )}
                        <td className="text-slate-500 text-[12.5px]">{b.updated_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={k === "pp_gold_seal" ? 9 : 9} className="text-center py-12 text-slate-500">
                          {k === "production"
                            ? "No production bulletins yet. Approve a PP/Gold Seal OB and transfer it to Production."
                            : "No PP/Gold Seal bulletins match filters."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
